import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Signup.css';
import { getApiUrl } from '../config/api';
import { roleLabel } from '../utils/roles';
import authFetch, { parseToken } from '../utils/authFetch';
import switchOrganization from '../utils/switchOrganization';

function formatPhoneDisplay(digits) {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function parsePhoneDigits(formatted) {
  return formatted.replace(/\D/g, '').slice(0, 10);
}

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState('');

  // A live session (present, unexpired token) turns the no-invite page into the
  // "create an additional organization" variant. An expired token counts as
  // logged out, matching InviteAccept's session check.
  const [loggedIn, setLoggedIn] = useState(false);
  const [accountFirstName, setAccountFirstName] = useState('');
  const [createdOrgName, setCreatedOrgName] = useState('');

  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
    sms_consent_transactional: false,
    sms_consent_proxy_rental: false
  });
  const [message, setMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (!inviteToken) return;

    const verifyInvite = async () => {
      try {
        const res = await fetch(
          getApiUrl(`/api/v1/organization/invite/verify?token=${encodeURIComponent(inviteToken)}`)
        );
        if (res.ok) {
          const data = await res.json();
          setInviteInfo(data);
          setFormData((prev) => ({ ...prev, email: data.email }));
        } else {
          const data = await res.json();
          setInviteError(data.error || 'Invalid or expired invitation link.');
        }
      } catch (err) {
        console.error('Error verifying invite:', err);
        setInviteError('Unable to verify invitation. Please try again.');
      } finally {
        setInviteLoading(false);
      }
    };

    verifyInvite();
  }, [inviteToken]);

  useEffect(() => {
    const payload = parseToken(localStorage.getItem('access_token'));
    const live = !!payload && Date.now() < payload.exp * 1000;
    setLoggedIn(live);
    if (live) {
      setAccountFirstName(localStorage.getItem('first_name') || '');
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validatePasswords = () => {
    if (formData.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Logged-in, no-invite: create an additional org. No password / SMS-consent
    // fields apply here, so skip those validations entirely.
    if (loggedIn && !inviteToken) {
      await handleExistingUserOrgCreate();
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    if ((formData.sms_consent_transactional || formData.sms_consent_proxy_rental) && !formData.phone.trim()) {
      setPhoneError('Phone number is required to enable SMS.');
      return;
    }
    setPhoneError('');

    if (inviteToken && inviteInfo) {
      await handleInviteJoin();
    } else {
      await handleOrgCreate();
    }
  };

  const handleExistingUserOrgCreate = async () => {
    const submitData = {
      company_name: formData.company_name,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
    };
    if (formData.phone) {
      submitData.phone = `+1${formData.phone}`;
    }

    try {
      const response = await authFetch(getApiUrl('/api/v1/organization/create'), {
        method: 'POST',
        body: JSON.stringify(submitData),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessage(data.error || 'Failed to create organization.');
        return;
      }

      setMessage('');

      // Auto-switch into the freshly created org so the user lands in it. If the
      // switch fails the org still exists, so show success and point them at the
      // switcher rather than surfacing an error.
      try {
        await switchOrganization(data.organization_id);
        window.location.assign('/dashboard');
      } catch (switchErr) {
        console.error('Error switching to new organization:', switchErr);
        setCreatedOrgName(data.org_name || 'Your organization');
        setSuccess(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    }
  };

  const handleInviteJoin = async () => {
    const submitData = {
      invite_token: inviteToken,
      first_name: formData.first_name,
      last_name: formData.last_name,
      password: formData.password,
      sms_consent_transactional: formData.sms_consent_transactional,
      sms_consent_proxy_rental: formData.sms_consent_proxy_rental,
    };
    if (formData.phone) {
      submitData.phone = `+1${formData.phone}`;
    }

    try {
      const response = await fetch(getApiUrl('/api/v1/organization/join'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to join organization.');
        return;
      }

      const data = await response.json();

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user_id', data.user_id);
      localStorage.setItem('first_name', data.first_name);

      setSuccess(true);
      setMessage('');
      setTimeout(() => { navigate('/dashboard'); }, 4000);
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    }
  };

  const handleOrgCreate = async () => {
    const submitData = { ...formData };
    delete submitData.confirm_password;
    if (submitData.phone) {
      submitData.phone = `+1${submitData.phone}`;
    }

    try {
      const response = await fetch(getApiUrl('/api/v1/organization/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to create organization.');
        return;
      }

      const loginResponse = await fetch(getApiUrl('/api/v1/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (loginResponse.ok) {
        const loginData = await loginResponse.json();
        
        localStorage.setItem('access_token', loginData.access_token);
        localStorage.setItem('refresh_token', loginData.refresh_token);
        localStorage.setItem('user_id', loginData.user_id);
        localStorage.setItem('first_name', loginData.first_name);
        
        setSuccess(true);
        setMessage('');
        setTimeout(() => { navigate('/dashboard'); }, 4000);
      } else {
        setMessage('Account created! Please log in.');
        setTimeout(() => { navigate('/login'); }, 1500);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    }
  };

  if (inviteLoading) {
    return (
      <div className="page-container">
        <div className="signup-wrapper">
          <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
          <div className="signup-box">
            <p className="text-center text-gray-600 py-8">Verifying invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteToken && inviteError) {
    return (
      <div className="page-container">
        <div className="signup-wrapper">
          <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
          <div className="signup-box">
            <div className="text-center py-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Invalid Invitation</h2>
              <p className="text-gray-600">{inviteError}</p>
              <button
                className="mt-6 px-6 py-2 bg-green-700 text-white rounded hover:bg-green-800"
                onClick={() => navigate('/signup')}
              >
                Create a new account instead
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Logged in without an invite: render the "create an additional organization"
  // variant. The invite flow always takes precedence (handled below).
  if (loggedIn && !inviteToken) {
    return (
      <div className="page-container">
        <div className="signup-wrapper">
          <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
          <div className="signup-box">
            {!success ? (
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Create a new organization</h2>
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                    {accountFirstName ? `${accountFirstName}, this` : 'This'} creats a new
                    organization linked to your current user account. Enter the new organization's
                    contact details below. The new organization is independent of your 
                    other organizations.
                  </div>
                </div>
                <input
                  type="text"
                  name="company_name"
                  placeholder="Company Name (optional)"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.company_name}
                  onChange={handleChange}
                />
                <div className="signup-name-row">
                  <input
                    type="text"
                    name="first_name"
                    placeholder="Contact First Name"
                    className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Contact Last Name"
                    className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Organization contact email@domain.com"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.email}
                  onChange={handleChange}
                />
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="text"
                    value="+1"
                    readOnly
                    className="w-[50px] p-2 border rounded bg-gray-100 text-gray-500 text-center cursor-not-allowed"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(555) 000-0000"
                    className="flex-1 p-2 border rounded focus:ring-2 focus:ring-green-400"
                    value={formatPhoneDisplay(formData.phone)}
                    onChange={(e) => setFormData({ ...formData, phone: parsePhoneDigits(e.target.value) })}
                  />
                </div>
                <button
                  type="submit"
                  className="mt-4 w-full p-2 bg-green-700 text-white rounded hover:bg-green-800"
                >Create organization</button>
                <p className="mt-4 text-center text-gray-600">
                  <span
                    className="text-green-700 cursor-pointer hover:underline"
                    onClick={() => navigate('/dashboard')}
                  >
                    Back to dashboard
                  </span>
                </p>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✓</div>
                <h2 className="text-xl font-semibold text-green-700">{createdOrgName} created</h2>
                <p className="text-gray-600 mt-2">
                  Use the organization switcher to open it.
                </p>
              </div>
            )}
          </div>
          {message && !success && <p className="error-message">{message}</p>}
        </div>
      </div>
    );
  }

  const isInviteFlow = inviteToken && inviteInfo;
  const roleDisplay = roleLabel(inviteInfo?.role);

  return (
    <div className="page-container">
      <div className="signup-wrapper">
        <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
        <div className="signup-box">
          {!success ? (
            <form onSubmit={handleSubmit}>
              {isInviteFlow ? (
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Join {inviteInfo.org_name}</h2>
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                    You've been invited to join <strong>{inviteInfo.org_name}</strong> as
                    a <strong>{roleDisplay}</strong>. Fill in your details below to create your account.
                  </div>
                </div>
              ) : (
                <h2 className="text-xl font-semibold text-gray-800">Create an account</h2>
              )}
              <div className="signup-name-row">
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.first_name}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
              {!isInviteFlow && (
                <input
                  type="text"
                  name="company_name"
                  placeholder="Company Name (optional)"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.company_name}
                  onChange={handleChange}
                />
              )}
              <input
                type="email"
                name="email"
                placeholder="email@domain.com"
                className={`mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400 ${
                  isInviteFlow ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                value={formData.email}
                onChange={handleChange}
                readOnly={isInviteFlow}
              />
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  value="+1"
                  readOnly
                  className="w-[50px] p-2 border rounded bg-gray-100 text-gray-500 text-center cursor-not-allowed"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="(555) 000-0000"
                  className="flex-1 p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formatPhoneDisplay(formData.phone)}
                  onChange={(e) => setFormData({ ...formData, phone: parsePhoneDigits(e.target.value) })}
                />
              </div>
              {phoneError && <p className="error-message">{phoneError}</p>}
              <input
                type="password"
                name="password"
                placeholder="Password (min 8 characters)"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.password}
                onChange={handleChange}
              />
              <input
                type="password"
                name="confirm_password"
                placeholder="Confirm Password"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.confirm_password}
                onChange={handleChange}
              />
              {passwordError && <p className="error-message">{passwordError}</p>}
              <div className="sms-consent-section">
                <div className="sms-consent-container">
                  <label className="sms-consent-label">
                    <input
                      type="checkbox"
                      name="sms_consent_transactional"
                      checked={formData.sms_consent_transactional}
                      onChange={handleChange}
                      className="sms-consent-checkbox"
                    />
                    <span className="sms-consent-text">
                      I agree to receive SMS from ZenRent for account and transactional notifications (e.g., verification, security alerts, important account updates). Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See{' '}
                      <a href="https://zenrent.net/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> &amp;{' '}
                      <a href="https://zenrent.net/terms" target="_blank" rel="noopener noreferrer">Terms</a>. Consent is not a condition of purchase.
                    </span>
                  </label>
                </div>
                <div className="sms-consent-container">
                  <label className="sms-consent-label">
                    <input
                      type="checkbox"
                      name="sms_consent_proxy_rental"
                      checked={formData.sms_consent_proxy_rental}
                      onChange={handleChange}
                      className="sms-consent-checkbox"
                    />
                    <span className="sms-consent-text">
                      I agree to send and receive SMS via ZenRent for rental communications, including messages exchanged with my landlord/tenant through ZenRent using proxy phone numbers (e.g., maintenance updates, scheduling, rental coordination). Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See{' '}
                      <a href="https://zenrent.net/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> &amp;{' '}
                      <a href="https://zenrent.net/terms" target="_blank" rel="noopener noreferrer">Terms</a>. Consent is not a condition of purchase.
                    </span>
                  </label>
                </div>
              </div>
              <button
                type="submit"
                className="mt-4 w-full p-2 bg-green-700 text-white rounded hover:bg-green-800"
              >{isInviteFlow ? 'Join Organization' : 'Sign Up'}</button>
              {isInviteFlow ? (
                <p className="mt-4 text-center text-gray-600">
                  Already have an account? <span
                    className="text-green-700 cursor-pointer hover:underline"
                    onClick={() => navigate(`/login?invite=${encodeURIComponent(inviteToken)}`)}
                  >
                    Log in to accept this invitation
                  </span>
                </p>
              ) : (
                <p className="mt-4 text-center text-gray-600">
                  Already have an account? <span
                    className="text-green-700 cursor-pointer hover:underline"
                    onClick={() => navigate('/login')}
                  >
                    Sign In
                  </span>
                </p>
              )}
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✓</div>
              <h2 className="text-xl font-semibold text-green-700">
                {isInviteFlow ? 'Welcome to the team!' : 'Account Created!'}
              </h2>
              <p className="text-gray-600 mt-2">Redirecting to your dashboard...</p>
            </div>
          )}
        </div>
        {message && !success && <p className={message.includes('successfully') ? 'success-message' : 'error-message'}>{message}</p>}
      </div>
    </div>
  );
};

export default Signup;
